/**
 * Review API client — wraps every review endpoint from the spec.
 */

import { api } from '@/lib/api'
import type {
  ReviewCriteriaList,
  ReviewCriterion,
  CreateReviewCriterionInput,
  UpdateReviewCriterionInput,
  ReviewRun,
  ReviewRunSummary,
  MergedReviewFinding,
  ReviewPermissions,
  ReviewIssueState,
  LatestCheck,
  StartReviewRunInput,
} from '@/lib/schemas'

// ===================
// Criteria
// ===================

export function listCriteria(): Promise<ReviewCriteriaList> {
  return api.get<ReviewCriteriaList>('/review-criteria')
}

export function createCriterion(input: CreateReviewCriterionInput): Promise<ReviewCriterion> {
  return api.post<ReviewCriterion>('/review-criteria', input)
}

export function updateCriterion(
  id: string,
  input: UpdateReviewCriterionInput,
): Promise<ReviewCriterion> {
  return api.patch<ReviewCriterion>(`/review-criteria/${id}`, input)
}

export function deleteCriterion(id: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/review-criteria/${id}`)
}

// ===================
// Permissions
// ===================

export function getReviewPermissions(
  bidId: string,
  artifactId: string,
  sectionKey?: string | null,
): Promise<ReviewPermissions> {
  const params: Record<string, string> = { bidId, artifactId }
  if (sectionKey) params['sectionKey'] = sectionKey
  return api.get<ReviewPermissions>('/review-permissions', params)
}

// ===================
// Runs
// ===================

export function startRun(input: StartReviewRunInput): Promise<{ runId: string; status: string }> {
  return api.post<{ runId: string; status: string }>('/review-runs', input)
}

export function getRun(id: string): Promise<ReviewRun> {
  return api.get<ReviewRun>(`/review-runs/${id}`)
}

export function getActiveFindings(artifactId: string): Promise<{ findings: MergedReviewFinding[] }> {
  return api.get<{ findings: MergedReviewFinding[] }>('/review-runs/active', { artifactId })
}

export function listRuns(
  artifactId: string,
  options: { includeDismissed?: boolean; limit?: number; offset?: number } = {},
): Promise<{ runs: ReviewRunSummary[] }> {
  const params: Record<string, string> = { artifactId }
  if (options.includeDismissed) params['includeDismissed'] = 'true'
  if (options.limit !== undefined) params['limit'] = String(options.limit)
  if (options.offset !== undefined) params['offset'] = String(options.offset)
  return api.get<{ runs: ReviewRunSummary[] }>('/review-runs', params)
}

/**
 * Latest check run for the artifact, plus whether the draft has changed since
 * it finished. Powers the "last checked" line in the full-response reader.
 */
export function getLatestCheck(artifactId: string): Promise<LatestCheck> {
  return api.get<LatestCheck>('/review-runs/latest', { artifactId })
}

// ===================
// Themes gate (scoped-brief §12.3) — latest Themes-criterion run for the artifact
// ===================

export interface LatestThemesRun {
  run: {
    id: string
    status: 'running' | 'complete' | 'failed'
    completedAt: string | null
    findingCount: number
  } | null
  /** Content changed since the run completed — the count may be out of date. */
  stale: boolean
}

export function getLatestThemesRun(artifactId: string): Promise<LatestThemesRun> {
  return api.get<LatestThemesRun>('/review-runs', { artifactId, themesLatest: '1' })
}

export function cancelRun(id: string): Promise<{ cancelled: boolean }> {
  return api.post<{ cancelled: boolean }>(`/review-runs/${id}/cancel`)
}

export function dismissRun(id: string): Promise<{ dismissed: boolean }> {
  return api.post<{ dismissed: boolean }>(`/review-runs/${id}/dismiss`)
}

export function restoreRun(id: string): Promise<{ restored: boolean }> {
  return api.post<{ restored: boolean }>(`/review-runs/${id}/restore`)
}

export function dismissAllFailed(artifactId: string): Promise<{ dismissed: number }> {
  return api.post<{ dismissed: number }>(`/review-runs/failed/dismiss-all?artifactId=${encodeURIComponent(artifactId)}`)
}

// ===================
// Issue actions — keyed by the stable issue identity, not (run, index)
// ===================

export function startFixAction(
  artifactId: string,
  issueKey: string,
): Promise<{ threadId: string; threadSeed: string }> {
  return api.post<{ threadId: string; threadSeed: string }>(
    `/artifacts/${artifactId}/review-issues/${issueKey}/action`,
  )
}

export function dismissIssue(
  artifactId: string,
  issueKey: string,
  reason?: string,
): Promise<ReviewIssueState> {
  return api.post<ReviewIssueState>(
    `/artifacts/${artifactId}/review-issues/${issueKey}/dismiss`,
    reason ? { reason } : {},
  )
}

/**
 * Manual status control: 'applied' = Mark as fixed (works with or without a
 * prior Fix-with-AI click), 'open' = Reopen.
 */
export function setIssueActionStatus(
  artifactId: string,
  issueKey: string,
  status: 'applied' | 'open',
): Promise<ReviewIssueState | { status: 'open' }> {
  return api.patch<ReviewIssueState | { status: 'open' }>(
    `/artifacts/${artifactId}/review-issues/${issueKey}/action`,
    { status },
  )
}

// Note: artifact-level reviewer permission/assignment endpoints were removed.
// Doc-level review now flows through:
//   POST /artifacts/{id}/submit-for-review (see api.ts: submitArtifactForReview)
//   POST /artifacts/{id}/reviews/{reviewId}/decide
//   GET /artifacts/{id}/reviews
//   POST /artifacts/{id}/cancel-review
